<template>
  <div class="searchFilter">
    <div class="searchFilter__top flex items-center gap-2">
      <div class="searchFilter__inputWrapper relative flex-1">
        <span class="fas fa-search searchFilter__icon absolute left-3 top-1/2 h-auto w-auto -translate-y-1/2 leading-none text-[15px]" />

        <input
          v-model="searchString"
          type="text"
          class="tb-input tb-input--search searchFilter__input w-full pl-[30px] pr-[20px]"
          placeholder="Search"
          data-test="producer-searchFilter-search"
        />

        <button
          v-show="searchString"
          class="searchFilter__clearSearch absolute right-3 top-1/2 -translate-y-1/2 rounded-full border-0 bg-transparent p-0 leading-none shadow-none appearance-none cursor-pointer"
          data-test="producer-searchFilter-clearSearch"
          @click="searchString = ''"
        >
          <span class="sr-only">Clear search term</span>
          <span class="fa fa-times" />
        </button>
      </div>
      <div
        v-if="!searchOnly"
        class="searchFilter__filterWrap relative overflow-visible"
        ref="filterWrap"
      >
        <hint-trigger text="Filter by tags">
          <button
            class="searchFilter__openTags inline-flex h-[2.25rem] w-[2.25rem] flex-shrink-0 items-center justify-center rounded-full border-0 bg-producer-slate-chip p-0 cursor-pointer"
            ref="openButton"
            data-test="producer-searchFilter-openTags"
            aria-haspopup="listbox"
            aria-label="Filter by tags"
            :aria-expanded="isPopupOpen"
            aria-controls="listbox"
            @click.stop="togglePopup"
          >
            <span class="sr-only">Filter by tags</span>
            <span class="fak fa-bars-filter-regular searchFilter__openTagsIcon text-[12px] leading-none"></span>
          </button>
        </hint-trigger>

        <!-- multiselect popup -->
        <div
          v-if="isPopupOpen"
          class="searchFilter__panel absolute right-0 top-full z-[100] flex max-h-[400px] min-w-full flex-col gap-3 bg-producer-slate-panel p-3 shadow-[0_12px_28px_rgba(0,0,0,0.28)]"
          v-click-outside="handleClickOutside"
          data-test="producer-searchFilter-popup"
        >
          <div class="searchFilter__popupHeader flex flex-wrap items-center gap-3">
            <div class="searchFilter__inputWrapper searchFilter__optionSearch relative min-w-[220px] flex-1">
              <span class="fas fa-search searchFilter__icon absolute left-3 top-1/2 h-auto w-auto -translate-y-1/2 leading-none text-[15px]" />
              <input
                v-model="filterTerm"
                type="text"
                class="tb-input tb-input--search searchFilter__input w-full pl-[30px] pr-[20px]"
                placeholder="Search"
                aria-label="Search tag options"
                data-test="producer-searchFilter-optionSearch"
              />
            </div>

            <div
              v-if="showSelectAll"
              class="tb-checkbox mb-0 searchFilter__selectAll flex flex-shrink-0 items-center gap-2"
            >
              <input
                v-model="allSelected"
                type="checkbox"
                :id="`tagSelectAll-${_uid}`"
                data-test="producer-searchFilter-selectAll"
              />

              <label class="checkbox sr-only" :for="`tagSelectAll-${_uid}`">Select all</label>
            </div>

            <hr class="searchFilter__divider m-0 w-full border-0 border-t border-t-white/10">
          </div>
          <ul
            id="listbox"
            class="searchFilter__optionList m-0 flex-1 list-none overflow-y-auto p-0"
            role="listbox"
            aria-multiselectable="true"
            aria-label="Option list"
          >
            <li
              v-for="o in filteredOptions"
              :key="o"
              class="searchFilter__optionListItem"
              role="option"
              :aria-label="o"
              :aria-selected="isOptionSelected(o)"
              :data-test="`producer-searchFilter-option:${o}`"
            >
              <div class="tb-checkbox">
                  <input
                    :checked="isOptionSelected(o)"
                    type="checkbox"
                    :id="o"
                    :data-test="`producer-searchFilter-checkBox:${o}`"
                    @change="onOptionSelect(o)"
                  />

                  <label :for="o">{{ o }}</label>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      v-show="!hideTags && selectedOptions.length"
      class="searchFilter__tags mt-4 flex flex-wrap gap-2"
    >
      <!-- Tag chips -->
      <span
        v-for="tag in selectedOptions"
        :key="tag"
        class="searchFilter__tag inline-flex items-center gap-2 rounded-full bg-producer-slate-chip px-2 py-1 text-base"
        :data-test="`producer-searchFilter-tag:${tag}`"
      >
        {{ tag }}
        <button
          class="searchFilter__tagClose flex h-5 w-5 items-center justify-center rounded-full border border-white/40 bg-transparent p-0 leading-none cursor-pointer"
          :data-test="`producer-searchFilter-tagClose:${tag}`"
          @click="removeSelectedOption(tag)"
        >
          <span class="fa fa-times searchFilter__tagCloseIcon" />
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import HintTrigger from './HintTrigger.vue';

export default {
  name: 'SearchFilter',

  components: {
    HintTrigger,
  },

  props: {
    value: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
    searchOnly: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: true,
    },
    hideTags: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPopupOpen: false,
      filterTerm: '',
    };
  },
  computed: {
    searchString: {
      get() {
        return this.value;
      },
      set: debounce(function debouncedSearchTermSetter(value) {
        this.$emit('update:value', value);
      }, 500),
    },

    filteredOptions() {
      if (!this.filterTerm) return this.options;

      const normalizedFilterTerm = this.filterTerm.toLocaleLowerCase();
      return this.options.filter((o) => o.toLocaleLowerCase().includes(normalizedFilterTerm));
    },

    optionsSet() {
      return new Set(this.options);
    },

    selectedOptionsSet() {
      return new Set(this.selectedOptions);
    },

    allSelected: {
      get() {
        if (!this.filteredOptions.length) {
          return false;
        }

        return this.filteredOptions.every((option) => this.selectedOptionsSet.has(option));
      },

      set(value) {
        const nextSelected = new Set(this.selectedOptionsSet);

        this.filteredOptions.forEach((option) => {
          if (value) {
            nextSelected.add(option);
          } else {
            nextSelected.delete(option);
          }
        });

        this.updateSelectedOptionsFromSet(nextSelected);
      },
    },

    selectedOptions: {
      get() {
        return this.selected;
      },
      set(value) {
        this.$emit('update:selected', value);
      },
    },
  },
  methods: {
    isOptionSelected(option) {
      return this.selectedOptionsSet.has(option);
    },

    removeSelectedOption(option) {
      const nextSelected = new Set(this.selectedOptionsSet);
      nextSelected.delete(option);
      this.updateSelectedOptionsFromSet(nextSelected);
    },

    updateSelectedOptionsFromSet(nextSelected) {
      const selectedOutsideOptions = this.selectedOptions.filter((option) => !this.optionsSet.has(option));
      const orderedSelectedOptions = this.options.filter((option) => nextSelected.has(option));

      this.selectedOptions = selectedOutsideOptions.concat(orderedSelectedOptions);
    },

    onOptionSelect(option) {
      const nextSelected = new Set(this.selectedOptionsSet);

      if (nextSelected.has(option)) {
        nextSelected.delete(option);
      } else {
        nextSelected.add(option);
      }

      this.updateSelectedOptionsFromSet(nextSelected);
    },
    togglePopup() {
      this.isPopupOpen = !this.isPopupOpen;
    },
    handleClickOutside(e) {
      const wrap = this.$refs.filterWrap;

      if (wrap?.contains(e.target)) {
        return;
      }

      this.isPopupOpen = false;
    },
  },
};
</script>

<style scoped>
.searchFilter__openTagsIcon {
  font-size: 0.75rem !important;
  line-height: 1;
  color: currentColor;
}

.searchFilter__tag {
  font-size: 0.8rem;
}

.searchFilter__tagClose {
  font-size: 0.6rem;
  border: 0;
}
</style>
